import { action } from '@reatom/framework'

import { Result } from '@blade/std-lib'

import { urlPair } from '../model/url-pair'

import { pairRegistry } from './pair-registry'
import { fetchPairRequestAction } from './pair-requests'

import type { PairModel } from './reatom-pair'

/**
 * The pair in the url, asked for before the application is built.
 *
 * Measured on staging, opening a pair page from cold: the document is parsed
 * at 649ms, the first request leaves at 1,326ms, `pair-info` leaves at
 * **2,368ms** and lands at 3,640ms. Everything on the page is behind it -
 * the trades, the holders, the chart - because each of them is a component
 * that takes a resolved pair as a prop, so none of them exists until the
 * pair does. The first candle was on screen at 5,339ms.
 *
 * Nothing in that second between the first request and `pair-info` was
 * waiting on an answer: the address was in the url before the document was.
 * It took that long because the request belongs to a component, the component
 * to a route, and the route to a React tree that is built after the session,
 * the theme, the locale and thirty initialisers.
 *
 * So the request is made from the url instead, as the first thing after the
 * api client exists, and the answer is put in the registry. The resource that
 * asks a second later finds it already there and makes no request at all. If
 * it asks while this one is still in flight, the coalescer in `pair-requests`
 * hands it the same promise.
 *
 * The pin is why the answer survives the wait: the registry holds weak
 * references, and in the second between this landing and the page reading it
 * nothing else refers to the model, so it could be collected before anybody
 * looks. One pair is held, and the next prefetch replaces it.
 */
let pinnedPair: PairModel | undefined

/** The prefetched pair, if the last prefetch found one. */
export const prefetchedUrlPair = () => pinnedPair

export const prefetchUrlPairAction = action((ctx) => {
  const target = urlPair(window.location.pathname)
  if (!target) {
    return
  }

  if (pairRegistry.get(target.address, target.network)) {
    return
  }

  ctx.schedule(async () => {
    const result = await fetchPairRequestAction(ctx, target)
    if (Result.isSuccess(result)) {
      pinnedPair = pairRegistry.resolveFromDto(ctx, result.success)
    }
  })
}, 'prefetchUrlPairAction')
